import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Alert,
	Autocomplete,
	Box,
	Button,
	Chip,
	FormControlLabel,
	Grid,
	IconButton,
	Paper,
	Switch,
	TextField,
	Tooltip,
	Typography,
	useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import BlockIcon from "@mui/icons-material/Block";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
	createLineDiscount,
	deactivateLineDiscount,
	getLineDiscounts,
	searchLineDiscountProducts,
	updateLineDiscount,
} from "../../services/lineDiscountService";
import { getCustomersCusSites } from "../../services/manageCustomerMasterService";
import { getAllCustomerSites } from "../../services/manageSitesMasterService";
import { getMachinesByProvision } from "../../services/analyticsService";
import { getProductsByMachineId, normalizeMachineProductsResponse } from "../../services/machineCardService";
import { getUserAllocations } from "../../services/manageSitesService";
import { useAuth } from "../../context/authContext";
import { formatDateTime } from "../../utilities/globalServiceUtils";
import Datagrid from "../Datagrid/Datagrid";
import DatagridLoading from "../DatagridLoading/DatagridLoading";
import ModalWrapper from "../ModalWrapper/ModalWrapper";
import UserName from "../UserName/UserName";

const EMPTY_FORM = {
	name: "",
	discount_value: "",
	customer_ids: [],
	customer_site_ids: [],
	machine_ids: [],
	product_ids: [],
	starts_at: "",
	ends_at: "",
	is_active: true,
};

const MAX_DISCOUNT_NAME_LENGTH = 50;

const EMPTY_BROAD_DISPLAY_STATE = {
	customer_ids: true,
	customer_site_ids: true,
	machine_ids: true,
	product_ids: true,
};

const EMPTY_CREATE_BROAD_DISPLAY_STATE = {
	...EMPTY_BROAD_DISPLAY_STATE,
	customer_ids: false,
	customer_site_ids: false,
};

const toIdArray = (value) =>
	Array.from(
		new Set(
			(Array.isArray(value) ? value : [])
				.map((item) => Number(item))
				.filter((item) => Number.isFinite(item) && item > 0)
		)
	);

const resolveScopedSiteIds = ({
	siteOptions = [],
	customerIds = [],
	explicitSiteIds = [],
} = {}) => {
	const normalizedExplicitSiteIds = toIdArray(explicitSiteIds);
	if (normalizedExplicitSiteIds.length) return normalizedExplicitSiteIds;

	const normalizedCustomerIds = new Set(toIdArray(customerIds));

	return toIdArray(
		siteOptions
			.filter((site) =>
				normalizedCustomerIds.size === 0 ||
				normalizedCustomerIds.has(Number(site.customer_id))
			)
			.map((site) => Number(site.value))
	);
};

const toInputDate = (value) => {
	if (!value) return "";
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
};
const toDayjsDate = (value) => {
	const normalized = toInputDate(value);
	return normalized ? dayjs(normalized) : null;
};

const formatDateDisplay = (value, emptyLabel = "N/A") => {
	const normalized = toInputDate(value);
	if (!normalized) return emptyLabel;
	return `${normalized.slice(5, 7)}-${normalized.slice(8, 10)}-${normalized.slice(0, 4)}`;
};

const isValidPercentageInput = (value) => /^\d+(\.\d+)?$/.test(String(value || "").trim());

const isValidDateInput = (value) => {
	if (!value) return false;
	if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value))) return false;

	const parsed = dayjs(value, "YYYY-MM-DD", true);
	return parsed.isValid();
};

const getTodayDate = () => {
	const today = new Date();
	const timezoneOffsetMs = today.getTimezoneOffset() * 60 * 1000;
	return new Date(today.getTime() - timezoneOffsetMs).toISOString().slice(0, 10);
};

const ADMIN_GRID_SX = {
	"& .MuiDataGrid-cell": {
		display: "flex",
		alignItems: "center",
		justifyContent: "flex-start",
		textAlign: "left",
	},
	"& .MuiDataGrid-columnHeaderTitleContainer": {
		justifyContent: "flex-start",
	},
	"& .MuiDataGrid-columnHeaderTitle": {
		textAlign: "left",
	},
};

const optionsByIds = (options = [], ids = []) => {
	const idSet = new Set(toIdArray(ids).map(String));
	return options.filter((option) => idSet.has(String(option.value)));
};

const optionsByValues = (options = [], values = []) => {
	const valueSet = new Set((Array.isArray(values) ? values : []).map(String));
	return options.filter((option) => valueSet.has(String(option.value)));
};

const selectedOrAllOptions = (options = [], ids = []) => {
	const normalizedIds = toIdArray(ids);
	return normalizedIds.length ? optionsByIds(options, normalizedIds) : [];
};

const selectedOrAllOptionsForDisplay = (options = [], ids = [], emptyMeansAll = false) => {
	const normalizedIds = toIdArray(ids);
	if (normalizedIds.length) return optionsByIds(options, normalizedIds);
	return emptyMeansAll ? options : [];
};

const getCompactSelectionTooltip = (selected = []) =>
	selected
		.map((option) => option?.label || option?.value)
		.filter(Boolean)
		.join(", ");

/**
 * Renders Autocomplete selections as a compact chip row ("A", "B", "+N more").
 *
 * @param {Array<{value: any, label: string, isAllOption?: boolean}>} value - Selected options.
 * @param {(params: {index: number}) => object} getTagProps - MUI tag props factory (key, onDelete, ...).
 * @param {string} allLabel - Label shown when everything is selected (collapses to a single chip).
 * @param {number} totalOptions - Option count, used to detect the "all selected" case.
 * @param {number} maxVisible - How many chips to show before collapsing the rest into "+N more".
 * @returns {JSX.Element|Array} Chip row, or [] when nothing is selected.
 *
 * Chips use `flex: 0 1 auto` on purpose: `1 1 0` made every chip stretch to an equal
 * share of the field once 2+ were selected, so short labels rendered as oversized pills.
 * Width is capped by maxWidth + ellipsis instead.
 */
const renderCompactTags = (
	value = [],
	getTagProps,
	allLabel,
	totalOptions = 0,
	maxVisible = 2
) => {
	const selected = Array.isArray(value) ? value : [];

	if (!selected.length) {
		return [];
	}

	if (selected.some((option) => option?.isAllOption)) {
		return (
			<Chip
				size="small"
				label={allLabel || "All"}
				{...getTagProps({ index: 0 })}
			/>
		);
	}

	if (allLabel && totalOptions > 1 && selected.length >= totalOptions) {
		return (
			<Chip
				size="small"
				label={allLabel}
				{...getTagProps({ index: 0 })}
			/>
		);
	}

	const visibleSelected = selected.slice(0, maxVisible);
	const hiddenCount = selected.length - visibleSelected.length;

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 0.5,
				overflow: "hidden",
				minWidth: 0,
				maxWidth: "100%",
			}}
		>
			{visibleSelected.map((option, index) => (
				<Chip
					key={option?.value || index}
					size="small"
					label={option?.label || option?.value}
					{...getTagProps({ index })}
					sx={{
						flex: "0 1 auto",
						minWidth: 0,
						maxWidth: { xs: 120, md: 140, xl: 180 },
						"& .MuiChip-label": {
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
						},
					}}
				/>
			))}

			{hiddenCount > 0 && (
				<Tooltip title={getCompactSelectionTooltip(selected)} arrow>
					<Chip
						size="small"
						label={`+${hiddenCount} more`}
						sx={{ cursor: "default", flexShrink: 0 }}
					/>
				</Tooltip>
			)}
		</Box>
	);
};

const labelsForIds = (options = [], ids = []) => {
	const optionMap = new Map(options.map((option) => [String(option.value), option.label]));
	return toIdArray(ids).map((id) => optionMap.get(String(id))).filter(Boolean);
};

const isAllScopeSelection = (options = [], ids = []) => {
	const normalizedIds = toIdArray(ids);
	return options.length > 1 && normalizedIds.length >= options.length;
};

const toProductOptions = (products = []) =>
	products
		.map((product) => ({
			value: Number(product.product_id),
			label: [product.product_number || product.pnum, product.product_name]
				.filter(Boolean)
				.join(" - ") || String(product.product_id),
		}))
		.filter((option) => Number.isFinite(option.value) && option.value > 0)
		.sort((left, right) =>
			left.label.localeCompare(right.label, undefined, {
				numeric: true,
				sensitivity: "base",
			})
		);

const mergeProductOptions = (...optionGroups) => {
	const byId = new Map();
	optionGroups.flat().forEach((option) => {
		if (option?.value) byId.set(Number(option.value), option);
	});
	return Array.from(byId.values()).sort((left, right) =>
		left.label.localeCompare(right.label, undefined, {
			numeric: true,
			sensitivity: "base",
		})
	);
};

const formatDiscount = (row) =>
	["percent", "percentage"].includes(String(row.discount_type || row.unit).toLowerCase())
		? `${row.discount_value}%`
		: row.discount_value;

const formatScopeList = (row, scopeOptions) => {
	const parts = [];
	const customerIds = toIdArray(row.customer_ids);
	const siteIds = toIdArray(row.customer_site_ids);
	const bakeries = labelsForIds(scopeOptions.customerOptions, row.customer_ids);
	const sites = labelsForIds(scopeOptions.siteOptions, row.customer_site_ids);
	const bakeriesAll = customerIds.length === 0 || isAllScopeSelection(scopeOptions.customerOptions, row.customer_ids);
	const sitesAll = siteIds.length === 0 || isAllScopeSelection(scopeOptions.siteOptions, row.customer_site_ids);

	if (bakeriesAll && sitesAll) {
		return ["All Bakeries", "All Bakery Locations"];
	}
	parts.push(bakeriesAll ? "All Bakeries" : `Bakery: ${bakeries.join(", ")}`);
	parts.push(sitesAll ? "All Bakery Locations" : `Location: ${sites.join(", ")}`);

	return parts.filter(Boolean);
};

const formatScope = (row, scopeOptions) => {
	return formatScopeList(row, scopeOptions).join(", ");
};

const getErrorMessage = (response, fallback) => {
	if (Array.isArray(response?.error)) return response.error.join(", ");
	if (Array.isArray(response?.data)) return response.data.join(", ");
	return response?.message || response?.error_message || fallback;
};

const getSavedLineDiscountRow = (response) =>
	response?.data && !Array.isArray(response.data) ? response.data : null;

const upsertSavedRow = (currentRows = [], savedRow = null) => {
	if (!savedRow?.id) return currentRows;
	const savedId = String(savedRow.id);
	return [savedRow, ...currentRows.filter((row) => String(row?.id) !== savedId)];
};

const LineDiscounts = () => {
	const { user, hasPermission } = useAuth();
	const compactTagLimit = useMediaQuery("(max-width:1366px)") ? 1 : 2;
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(false);
	const [loadingFilters, setLoadingFilters] = useState(false);
	const [scopeLookupsReady, setScopeLookupsReady] = useState(false);
	const [error, setError] = useState("");
	const [modalOpen, setModalOpen] = useState(false);
	const [editRow, setEditRow] = useState(null);
	const [detailRow, setDetailRow] = useState(null);
	const [scrollToRowId, setScrollToRowId] = useState(null);
	const [saving, setSaving] = useState(false);
	const [form, setForm] = useState(EMPTY_FORM);
	const [broadDisplayState, setBroadDisplayState] = useState(EMPTY_BROAD_DISPLAY_STATE);
	const [fieldErrors, setFieldErrors] = useState({});
	const [customerOptions, setCustomerOptions] = useState([]);
	const [siteOptions, setSiteOptions] = useState([]);
	const [machineOptions, setMachineOptions] = useState([]);
	const [allProductOptions, setAllProductOptions] = useState([]);
	const [productOptions, setProductOptions] = useState([]);
	const [loadingProducts, setLoadingProducts] = useState(false);
	const containerRef = useRef(null);
	const formRef = useRef(null);
	const todayDate = useMemo(() => getTodayDate(), []);
	const isSuperAdmin = Number(user?.user_id) === 1;
	const canCreate = isSuperAdmin || hasPermission("line_discount_management.create_line_discounts");
	const canEdit = isSuperAdmin || hasPermission("line_discount_management.edit_line_discounts");
	const canDelete = isSuperAdmin || hasPermission("line_discount_management.delete_line_discounts");
	const actorRoleType = useMemo(
		() =>
			String(
				user?.role?.role_type ||
				user?.role?.role_name ||
				user?.user_role_name ||
				user?.user_role ||
				""
			)
				.trim()
				.toLowerCase(),
		[user?.role?.role_type, user?.role?.role_name, user?.user_role_name, user?.user_role]
	);
	const directActorSiteIds = useMemo(
		() =>
			Array.from(
				new Set(
					(Array.isArray(user?.allocations) ? user.allocations : [])
						.map((allocation) => Number(allocation?.site_id))
						.filter((id) => Number.isFinite(id) && id > 0)
				)
			),
		[user?.allocations]
	);
	const [fallbackActorSiteIds, setFallbackActorSiteIds] = useState([]);
	useEffect(() => {
		let mounted = true;
		const loadFallbackActorScope = async () => {
			if (isSuperAdmin || directActorSiteIds.length > 0 || !user?.user_id) {
				if (mounted) setFallbackActorSiteIds([]);
				return;
			}
			try {
				const response = await getUserAllocations(user.user_id);
				const allocations = Array.isArray(response?.data?.allocations) ? response.data.allocations : [];
				const nextIds = Array.from(
					new Set(
						allocations
							.map((allocation) => Number(allocation?.site_id))
							.filter((id) => Number.isFinite(id) && id > 0)
					)
				);
				if (mounted) setFallbackActorSiteIds(nextIds);
			} catch (_error) {
				if (mounted) setFallbackActorSiteIds([]);
			}
		};
		loadFallbackActorScope();
		return () => {
			mounted = false;
		};
	}, [directActorSiteIds, isSuperAdmin, user?.user_id]);
	const effectiveActorSiteIds = useMemo(
		() => (directActorSiteIds.length > 0 ? directActorSiteIds : fallbackActorSiteIds),
		[directActorSiteIds, fallbackActorSiteIds]
	);
	// Mirrors actorHasAllSiteVisibilityForFilters in PromotionCodes.js — keep the two in
	// step, they answer the same question.
	//
	// This used to also short-circuit on `line_discount_management.view_line_discounts`,
	// which disabled site scoping entirely: the server only lets a caller list rules at
	// all if they hold one of the four line_discount_management permissions, so every
	// non-super-admin who could open this page satisfied that clause and saw every
	// customer's rules, sites and machines. Viewing the feature is not the same as being
	// allocated to every site — only an admin/manage_all_sites actor with no explicit
	// allocations gets that.
	const hasFullVisibilityForFilters = useMemo(
		() =>
			isSuperAdmin ||
			((actorRoleType === "admin" || hasPermission("user_management.manage_all_sites")) &&
				effectiveActorSiteIds.length === 0),
		[actorRoleType, effectiveActorSiteIds.length, hasPermission, isSuperAdmin]
	);
	const scopedRows = useMemo(() => {
		if (hasFullVisibilityForFilters) return rows;
		const allowedSiteIds = new Set(effectiveActorSiteIds.map((id) => Number(id)));
		return rows.filter((row) => {
			const scopedSiteIds = toIdArray(row?.customer_site_ids);
			return scopedSiteIds.length > 0 && scopedSiteIds.every((siteId) => allowedSiteIds.has(Number(siteId)));
		});
	}, [effectiveActorSiteIds, hasFullVisibilityForFilters, rows]);

	const modalAutocompleteProps = useMemo(
		() => ({
			disablePortal: false,
			disableCloseOnSelect: true,
			PopperProps: {
				className: "modal-menu-popover",
				sx: { zIndex: 2000 },
				modifiers: [
					{ name: "preventOverflow", options: { boundary: "viewport", padding: 8 } },
					{ name: "flip", options: { padding: 8 } },
				],
			},
			slotProps: {
				popper: { className: "modal-menu-popover", sx: { zIndex: 2000 } },
				paper: { sx: { maxHeight: 320, overflowY: "auto" } },
				listbox: { sx: { maxHeight: 300, overflowY: "auto" } },
			},
			ListboxProps: { style: { maxHeight: 300, overflowY: "auto" } },
		}),
		[]
	);

	const fetchRules = useCallback(async ({ silent = false } = {}) => {
		if (!silent) setLoading(true);
		setError("");
		try {
			const response = await getLineDiscounts();
			if (response?.status === false) {
				setError(getErrorMessage(response, "Failed to load line discounts"));
				setRows([]);
				return;
			}
			const nextRows = Array.isArray(response?.data) ? response.data : [];
			setRows(nextRows);

			const selectedProductIds = toIdArray(
				nextRows.flatMap((rule) => rule.product_ids || [])
			);
			if (selectedProductIds.length) {
				try {
					const productsResponse = await searchLineDiscountProducts({
						ids: selectedProductIds,
					});
					const selectedOptions = toProductOptions(productsResponse?.data || []);
					setAllProductOptions((previous) =>
						mergeProductOptions(previous, selectedOptions)
					);
				} catch (productError) {
					console.warn("Failed to load line discount product labels:", productError);
				}
			}
		} catch (err) {
			setError(err?.message || "Failed to load line discounts");
			setRows([]);
		} finally {
			if (!silent) setLoading(false);
		}
	}, []);

	const fetchOptions = useCallback(async () => {
		const [customersResponse, sitesResponse, machinesResponse] = await Promise.allSettled([
			getCustomersCusSites(),
			getAllCustomerSites(),
			getMachinesByProvision(),
		]);

		const sites = sitesResponse.value?.data || [];
		const visibleSites = hasFullVisibilityForFilters
			? sites
			: sites.filter((site) => effectiveActorSiteIds.includes(Number(site.site_id)));
		const eligibleSiteIds = new Set(visibleSites.map((site) => Number(site.site_id)));
		const eligibleCustomerIds = new Set(
			visibleSites
				.map((site) => Number(site.cus_id))
				.filter((id) => Number.isFinite(id) && id > 0)
		);

		const customers = customersResponse.value?.data || [];
		const visibleCustomers = customers.filter((customer) =>
			eligibleCustomerIds.has(Number(customer.cus_id))
		);
		setCustomerOptions(
			visibleCustomers
				.filter((customer) => customer.customer_name && customer.customer_name !== "EMPTY")
				.map((customer) => ({
					value: customer.cus_id,
					label: `${customer.customer_name} - ${customer.customer_number}`,
				}))
		);

		setSiteOptions(
			visibleSites
				.filter((site) => site.customer_site_name && site.customer_site_name !== "EMPTY")
				.map((site) => ({
					value: site.site_id,
					customer_id: site.cus_id,
					site_number: site.customer_site_number || site.site_number,
					label: `${site.customer_site_name} - ${site.customer_site_number}`,
				}))
		);

		const machines = machinesResponse.value?.data || [];
		const visibleMachines = hasFullVisibilityForFilters
			? machines
			: machines.filter((machine) => effectiveActorSiteIds.includes(Number(machine.site_id)));
		setMachineOptions(
			visibleMachines
				.filter((machine) => eligibleSiteIds.has(Number(machine.site_id)))
				.map((machine) => ({
					value: machine.machine_id,
					customer_id: machine.cus_id,
					site_id: machine.site_id,
					machine_number: machine.machine_number,
					machine_name: machine.machine_name,
					site_number: machine.customer_site_number || machine.site_number,
					label: `${machine.machine_name || "Machine"} - ${machine.machine_number}`,
				}))
		);

	}, [
		effectiveActorSiteIds,
		hasFullVisibilityForFilters,
	]);

	useEffect(() => {
		fetchRules();
	}, [fetchRules]);

	useEffect(() => {
		if (!user) return;
		const init = async () => {
			setLoadingFilters(true);
			setScopeLookupsReady(false);
			await fetchOptions();
			setScopeLookupsReady(true);
			setLoadingFilters(false);
		};
		init();
	}, [fetchOptions, user]);

	useEffect(() => {
		if (!modalOpen) return;
		let frameOne = 0;
		let frameTwo = 0;
		frameOne = window.requestAnimationFrame(() => {
			containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
			frameTwo = window.requestAnimationFrame(() => {
				formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
			});
		});
		return () => {
			window.cancelAnimationFrame(frameOne);
			window.cancelAnimationFrame(frameTwo);
		};
	}, [modalOpen, editRow?.id]);

	const scopedCustomerOptions = useMemo(() => {
		return customerOptions;
	}, [customerOptions]);

	const scopedSiteOptions = useMemo(() => {
		const customerIds = toIdArray(form.customer_ids);
		return siteOptions.filter((site) =>
			!customerIds.length || customerIds.includes(Number(site.customer_id))
		);
	}, [form.customer_ids, siteOptions]);

	const scopedMachineOptions = useMemo(() => {
		const customerIds = toIdArray(form.customer_ids);
		const siteIds = toIdArray(form.customer_site_ids);

		return machineOptions.filter((machine) => {
			if (siteIds.length) return siteIds.includes(Number(machine.site_id));
			if (customerIds.length) {
				return customerIds.includes(Number(machine.customer_id));
			}
			return true;
		});
	}, [
		form.customer_ids,
		form.customer_site_ids,
		machineOptions,
	]);

	useEffect(() => {
		if (!modalOpen) return;

		const machineIds = toIdArray(form.machine_ids);
		if (!machineIds.length) {
			setProductOptions([]);
			setForm((prev) =>
				toIdArray(prev.product_ids).length ? { ...prev, product_ids: [] } : prev
			);
			return;
		}

		let active = true;
		const selectedMachines = machineOptions.filter((machine) =>
			machineIds.includes(Number(machine.value))
		);

		const loadProducts = async () => {
			setLoadingProducts(true);
			try {
				const resultGroups = await Promise.all(
					selectedMachines.map(async (machine) => {
						const site = siteOptions.find(
							(option) => Number(option.value) === Number(machine.site_id)
						);
						const siteNumber = site?.site_number || machine.site_number;
						if (!machine.value || !siteNumber) return [];

						const response = await getProductsByMachineId({
							machine_id: machine.value,
							site_number: siteNumber,
						});
						const { products } = normalizeMachineProductsResponse(response);
						return products || [];
					})
				);

				if (!active) return;

				const byProductId = new Map();
				resultGroups.flat().forEach((product) => {
					const productId = Number(product?.product_id);
					if (!Number.isFinite(productId) || productId <= 0) return;
					if (!byProductId.has(productId)) {
						byProductId.set(productId, {
							value: productId,
							label: [product.product_number || product.pnum, product.product_name]
								.filter(Boolean)
								.join(" - ") || String(productId),
						});
					}
				});

				const nextOptions = mergeProductOptions(Array.from(byProductId.values()));
				const availableIds = new Set(nextOptions.map((option) => Number(option.value)));
				setProductOptions(nextOptions);
				setAllProductOptions((previous) =>
					mergeProductOptions(previous, nextOptions)
				);
				setForm((prev) => {
					const selectedIds = toIdArray(prev.product_ids);
					const validIds = selectedIds.filter((id) => availableIds.has(id));
					return validIds.length === selectedIds.length
						? prev
						: { ...prev, product_ids: validIds };
				});
			} catch (err) {
				if (active) setProductOptions([]);
			} finally {
				if (active) setLoadingProducts(false);
			}
		};

		loadProducts();
		return () => {
			active = false;
		};
	}, [form.machine_ids, machineOptions, modalOpen, siteOptions]);

	const displayRows = useMemo(
		() =>
			scopedRows.map((row) => ({
				...row,
				discount_display: formatDiscount(row),
				scope_display: formatScope(row, {
					customerOptions,
					siteOptions,
					machineOptions,
					productOptions: allProductOptions,
				}),
			})),
		[allProductOptions, customerOptions, machineOptions, scopedRows, siteOptions]
	);

	const getLineDiscountScopeDetails = useCallback((row) => {
		return {
			bakeries: isAllScopeSelection(customerOptions, row.customer_ids) ? [] : labelsForIds(customerOptions, row.customer_ids),
			locations: isAllScopeSelection(siteOptions, row.customer_site_ids) ? [] : labelsForIds(siteOptions, row.customer_site_ids),
			machines: isAllScopeSelection(machineOptions, row.machine_ids) ? [] : labelsForIds(machineOptions, row.machine_ids),
			parts: isAllScopeSelection(allProductOptions, row.product_ids) ? [] : labelsForIds(allProductOptions, row.product_ids),
		};
	}, [allProductOptions, customerOptions, machineOptions, siteOptions]);

	const openCreate = () => {
		setDetailRow(null);
		setEditRow(null);
		setForm(EMPTY_FORM);
		setBroadDisplayState(EMPTY_CREATE_BROAD_DISPLAY_STATE);
		setProductOptions([]);
		setFieldErrors({});
		setModalOpen(true);
	};

	const openEdit = (row) => {
		const nextForm = {
			name: row.name || "",
			discount_value: row.discount_value ?? "",
			customer_ids: toIdArray(row.customer_ids),
			customer_site_ids: toIdArray(row.customer_site_ids),
			machine_ids: toIdArray(row.machine_ids),
			product_ids: toIdArray(row.product_ids),
			starts_at: toInputDate(row.starts_at),
			ends_at: toInputDate(row.ends_at),
			is_active: Boolean(row.is_active),
		};
		setDetailRow(null);
		setEditRow(row);
		setForm(nextForm);
		setBroadDisplayState({
			customer_ids: nextForm.customer_ids.length === 0,
			customer_site_ids: nextForm.customer_site_ids.length === 0,
			machine_ids: nextForm.machine_ids.length === 0,
			product_ids: nextForm.product_ids.length === 0,
		});
		setFieldErrors({});
		setModalOpen(true);
	};

	const closeForm = () => {
		setModalOpen(false);
		setEditRow(null);
		setForm(EMPTY_FORM);
		setBroadDisplayState(EMPTY_CREATE_BROAD_DISPLAY_STATE);
		setProductOptions([]);
		setFieldErrors({});
	};

	const openDetail = (row) => {
		if (!row) return;
		setDetailRow(row);
	};

	const closeDetail = () => {
		setDetailRow(null);
	};

	const updateFormField = (field, value) => {
		setForm((prev) => ({ ...prev, [field]: value }));
		setFieldErrors((prev) => {
			if (!prev[field] && !(field === "starts_at" && prev.ends_at)) return prev;
			const next = { ...prev };
			delete next[field];
			if (field === "starts_at") delete next.ends_at;
			return next;
		});
	};

	const handleCustomersChange = (nextValue = [], changeMeta = {}) => {
		if (changeMeta?.isAllSelection) {
			setBroadDisplayState((prev) => ({
				...prev,
				customer_ids: true,
				customer_site_ids: true,
				machine_ids: true,
				product_ids: true,
			}));
			setForm((prev) => ({
				...prev,
				customer_ids: [],
				customer_site_ids: [],
				machine_ids: [],
				product_ids: [],
			}));
			return;
		}

		setBroadDisplayState((prev) => ({
			...prev,
			customer_ids: false,
			customer_site_ids: false,
			machine_ids: false,
			product_ids: false,
		}));
		const customerIds = toIdArray(nextValue.map((option) => option.value));

		const validSiteIds = customerIds.length
			? siteOptions
				.filter((site) => customerIds.includes(Number(site.customer_id)))
				.map((site) => Number(site.value))
			: [];

		const validMachineIds = customerIds.length
			? machineOptions
				.filter((machine) => customerIds.includes(Number(machine.customer_id)))
				.map((machine) => Number(machine.value))
			: [];

		setForm((prev) => ({
			...prev,
			customer_ids: customerIds,

			// Keep only selected locations/machines that still belong to selected bakeries.
			customer_site_ids: customerIds.length
				? toIdArray(prev.customer_site_ids).filter((siteId) =>
					validSiteIds.includes(siteId)
				)
				: [],

			machine_ids: customerIds.length
				? toIdArray(prev.machine_ids).filter((machineId) =>
					validMachineIds.includes(machineId)
				)
				: [],

			product_ids: [],
		}));
		setFieldErrors((prev) => {
			const next = { ...prev };
			delete next.customer_ids;
			delete next.customer_site_ids;
			delete next.machine_ids;
			delete next.product_ids;
			return next;
		});
	};

	// ─── Utility: merge-update broadDisplayState safely ─────────────────────────
	const mergeBroad = (updates) =>
		setBroadDisplayState((prev) => ({ ...prev, ...updates }));

	// ─── handleLocationsChange ────────────────────────────────────────────────────
	const handleLocationsChange = (nextValue = [], changeMeta = {}) => {
		if (changeMeta?.isAllSelection) {
			mergeBroad({
				// Do NOT reset customer_ids — bakery selection must survive
				customer_site_ids: true,
				machine_ids: true,
				product_ids: true,
			});
			setForm((prev) => ({
				...prev,
				// customer_ids intentionally kept — "All Locations" within selected bakeries
				customer_site_ids: [],
				machine_ids: [],
				product_ids: [],
			}));
			setFieldErrors((prev) => {
				const next = { ...prev };
				delete next.customer_site_ids;
				delete next.machine_ids;
				delete next.product_ids;
				return next;
			});
			return;
		}

		mergeBroad({
			customer_site_ids: false,
			machine_ids: false,
			product_ids: false,
		});

		const siteIds = toIdArray(nextValue.map((option) => option.value));
		const selectedSites = siteOptions.filter((site) =>
			siteIds.includes(Number(site.value))
		);
		const relatedCustomerIds = toIdArray(selectedSites.map((site) => site.customer_id));

		const validMachineIds = siteIds.length
			? machineOptions
				.filter((machine) => siteIds.includes(Number(machine.site_id)))
				.map((machine) => Number(machine.value))
			: [];

		setForm((prev) => {
			// Keep existing customer_ids if they're a superset of what the sites imply;
			// only auto-fill if no bakeries were previously selected.
			const prevCustomerIds = toIdArray(prev.customer_ids);
			const nextCustomerIds =
				siteIds.length && prevCustomerIds.length === 0
					? relatedCustomerIds
					: prevCustomerIds.length > 0
						? prevCustomerIds // ← preserve explicit bakery selection
						: relatedCustomerIds;
			return {
				...prev,
				customer_ids: nextCustomerIds,
				customer_site_ids: siteIds,
				machine_ids: toIdArray(prev.machine_ids).filter((machineId) =>
					validMachineIds.includes(machineId)
				),
				product_ids: [],
			};
		});

		setFieldErrors((prev) => {
			const next = { ...prev };
			delete next.customer_ids;
			delete next.customer_site_ids;
			delete next.machine_ids;
			delete next.product_ids;
			return next;
		});
	};

	// ─── handleMachinesChange ─────────────────────────────────────────────────────
	const handleMachinesChange = (nextValue = [], changeMeta = {}) => {
		if (changeMeta?.isAllSelection) {
			mergeBroad({ machine_ids: true, product_ids: true });
			setForm((prev) => ({ ...prev, machine_ids: [], product_ids: [] }));
			return;
		}

		mergeBroad({ machine_ids: false, product_ids: false });
		const machineIds = toIdArray(nextValue.map((option) => option.value));
		const selectedMachines = machineOptions.filter((machine) =>
			machineIds.includes(Number(machine.value))
		);

		setForm((prev) => {
			// Derive implied scope, but only EXPAND upward — never shrink prior selections
			const impliedSiteIds = toIdArray(selectedMachines.map((m) => m.site_id));
			const impliedCustomerIds = toIdArray(selectedMachines.map((m) => m.customer_id));

			const prevSiteIds = toIdArray(prev.customer_site_ids);
			const prevCustomerIds = toIdArray(prev.customer_ids);

			// Merge implied into existing — machines should not shrink bakery/location scope
			const nextSiteIds =
				prevSiteIds.length > 0
					? Array.from(new Set([...prevSiteIds, ...impliedSiteIds]))
					: impliedSiteIds;

			const nextCustomerIds =
				prevCustomerIds.length > 0
					? Array.from(new Set([...prevCustomerIds, ...impliedCustomerIds]))
					: impliedCustomerIds;

			return {
				...prev,
				customer_ids: machineIds.length ? nextCustomerIds : prev.customer_ids,
				customer_site_ids: machineIds.length ? nextSiteIds : prev.customer_site_ids,
				machine_ids: machineIds,
				product_ids: [],
			};
		});

		setFieldErrors((prev) => {
			const next = { ...prev };
			["customer_ids", "customer_site_ids", "machine_ids", "product_ids"]
				.forEach((key) => delete next[key]);
			return next;
		});
	};

	// ─── hydrateLineDiscountFieldFromAll (cascade fix) ───────────────────────────
	const hydrateLineDiscountFieldFromAll = useCallback(
		(field) => {
			if (field === "customer_ids" && broadDisplayState.customer_ids) {
				mergeBroad({ customer_ids: false });
				setForm((prev) => ({
					...prev,
					customer_ids: scopedCustomerOptions.map((o) => o.value),
					// Reset downstream when hydrating bakery — their "All" state still covers them
					machine_ids: [],
					product_ids: [],
				}));
				mergeBroad({ machine_ids: true, product_ids: true }); // keep "All" display
				return;
			}
			if (field === "customer_site_ids" && broadDisplayState.customer_site_ids) {
				mergeBroad({ customer_site_ids: false });
				setForm((prev) => ({
					...prev,
					customer_site_ids: scopedSiteOptions.map((o) => o.value),
					machine_ids: [],
					product_ids: [],
				}));
				mergeBroad({ machine_ids: true, product_ids: true });
				return;
			}
			if (field === "machine_ids" && broadDisplayState.machine_ids) {
				mergeBroad({ machine_ids: false });
				setForm((prev) => ({
					...prev,
					machine_ids: scopedMachineOptions.map((o) => o.value),
					product_ids: [],
				}));
				mergeBroad({ product_ids: true });
				return;
			}
			if (field === "product_ids" && broadDisplayState.product_ids) {
				mergeBroad({ product_ids: false });
				setForm((prev) => ({
					...prev,
					product_ids: productOptions.map((o) => o.value),
				}));
			}
		},
		[
			broadDisplayState,
			productOptions,
			scopedCustomerOptions,
			scopedMachineOptions,
			scopedSiteOptions,
		]
	);
	const saveRule = async () => {
		const errors = {};
		const discountName = String(form.name || "").trim();
		if (!discountName) errors.name = "Discount name is required.";
		else if (discountName.length > MAX_DISCOUNT_NAME_LENGTH) {
			errors.name = `Max length is ${MAX_DISCOUNT_NAME_LENGTH} char`;
		}

		const rawDiscountValue = String(form.discount_value || "").trim();
		const discountValue = Number(rawDiscountValue);
		if (!rawDiscountValue) {
			errors.discount_value = "Discount percentage is required.";
		} else if (!isValidPercentageInput(rawDiscountValue)) {
			errors.discount_value = "Discount percentage must be a valid number.";
		} else if (!Number.isFinite(discountValue) || discountValue <= 0 || discountValue > 100) {
			errors.discount_value = "Discount percentage must be between 0 and 100.";
		}
		if (!form.starts_at) {
			errors.starts_at = "Start date is required.";
		} else if (!isValidDateInput(form.starts_at)) {
			errors.starts_at = "Start date must be a valid date.";
		} else if (form.starts_at < todayDate) {
			errors.starts_at = "Start date cannot be earlier than today.";
		}

		if (!form.ends_at) {
			errors.ends_at = "End date is required.";
		} else if (!isValidDateInput(form.ends_at)) {
			errors.ends_at = "End date must be a valid date.";
		} else if (form.ends_at < todayDate) {
			errors.ends_at = "End date cannot be earlier than today.";
		} else if (form.starts_at && form.ends_at < form.starts_at) {
			errors.ends_at = "End date must be after start date.";
		}
		if (Object.keys(errors).length > 0) {
			setFieldErrors(errors);
			return;
		}

		setSaving(true);
		setFieldErrors({});
		try {
			const resolvedSiteIds = resolveScopedSiteIds({
				siteOptions,
				customerIds: form.customer_ids,
				explicitSiteIds: form.customer_site_ids,
			});
			const payload = {
				...form,
				discount_type: "percent",
				discount_value: discountValue,
				customer_site_ids: resolvedSiteIds,
				machine_ids: [],
				product_ids: [],
			};
			const response = editRow?.id
				? await updateLineDiscount(editRow.id, payload)
				: await createLineDiscount(payload);
			if (response?.status === false) {
				Swal.fire("Error", getErrorMessage(response, "Failed to save line discount"), "error");
				return;
			}
			const savedRow = getSavedLineDiscountRow(response);
			if (savedRow) {
				setRows((currentRows) => upsertSavedRow(currentRows, savedRow));
				setScrollToRowId(null);
				window.requestAnimationFrame(() => setScrollToRowId(savedRow.id));
			}
			closeForm();
			await fetchRules({ silent: true });
			Swal.fire("Saved", "Line discount rule saved successfully", "success");
		} catch (err) {
			Swal.fire("Error", err?.message || "Failed to save line discount", "error");
		} finally {
			setSaving(false);
		}
	};

	const deactivateRule = async (row) => {
		const confirmation = await Swal.fire({
			title: "Deactivate line discount?",
			text: row.name,
			icon: "warning",
			showCancelButton: true,
			confirmButtonText: "Deactivate",
		});
		if (!confirmation.isConfirmed) return;
		const response = await deactivateLineDiscount(row.id);
		if (response?.status === false) {
			Swal.fire("Error", getErrorMessage(response, "Failed to deactivate line discount"), "error");
			return;
		}
		await fetchRules({ silent: true });
		if (detailRow?.id === row.id) closeDetail();
		if (editRow?.id === row.id) closeForm();
	};

	const renderMultiSelect = ({
		label,
		options,
		value,
		onChange,
		onOpen,
		loading = false,
		disabled = false,
		allLabel = "",
		isAllSelected = false,
		error = "",
		required = false,
	}) => {
		const allOption = allLabel
			? {
				value: `__ALL__${label}`,
				label: allLabel,
				isAllOption: true,
			}
			: null;

		const enhancedOptions = allOption ? [allOption, ...options] : options;
		const effectiveValue = isAllSelected && allOption ? [allOption] : value;

		return (
			<Autocomplete
				{...modalAutocompleteProps}
				multiple
				disableCloseOnSelect
				size="small"
				options={enhancedOptions}
				value={effectiveValue}
				onChange={(_event, nextValue) => {
					const selectedValues = Array.isArray(nextValue) ? nextValue : [];

					const selectedAllOption = selectedValues.some(
						(option) => option?.isAllOption === true
					);

					const selectedSpecificOptions = selectedValues.filter(
						(option) => option?.isAllOption !== true
					);

					if (selectedAllOption && selectedSpecificOptions.length === 0) {
						onChange([], { isAllSelection: true });
						return;
					}

					onChange(selectedSpecificOptions);
				}}
				onOpen={onOpen}
				loading={loading}
				getOptionLabel={(option) => option?.label || ""}
				isOptionEqualToValue={(option, selected) =>
					String(option?.value) === String(selected?.value)
				}
				renderTags={(selected, getTagProps) =>
					renderCompactTags(selected, getTagProps, allLabel, options.length, compactTagLimit)
				}
				renderInput={(params) => (
					<TextField
						{...params}
						label={label}
						required={required}
						error={Boolean(error)}
						helperText={error}
						placeholder={
							!effectiveValue || effectiveValue.length === 0
								? `Select ${label}`
								: ""
						}
					/>
				)}
				disabled={disabled}
			/>
		);
	};

	const lineDiscountMinDate = todayDate;
	const columns = [
		{
			field: "name",
			headerName: "Name",
			flex: 1.3,
			minWidth: 220,
			renderCell: (params) => (
				<Box sx={{ minWidth: 0 }}>
					<Typography variant="body2" fontWeight={600} noWrap>
						{params.row?.name}
					</Typography>
				</Box>
			),
		},
		{
			field: "discount_display",
			headerName: "Discount",
			width: 140,
			renderCell: (params) => (
				<Chip size="small" color="primary" variant="outlined" label={params.row?.discount_display} />
			),
		},
		{
			field: "scope_display",
			headerName: "Scope",
			flex: 2,
			minWidth: 300,
			renderCell: (params) => (
				<Tooltip title={params.row?.scope_display || "-"} arrow>
					<Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
						{params.row?.scope_display}
					</Typography>
				</Tooltip>
			),
		},
		{
			field: "starts_at",
			headerName: "Start Date",
			flex: 0.8,
			minWidth: 140,
			valueFormatter: (value) => formatDateDisplay(value, "N/A"),
		},
		{
			field: "ends_at",
			headerName: "End Date",
			flex: 0.8,
			minWidth: 140,
			valueFormatter: (value) => formatDateDisplay(value, "N/A"),
		},
		{
			field: "is_active",
			headerName: "Status",
			width: 120,
			renderCell: (params) => (
				<Chip
					size="small"
					color={params.row?.is_active ? "success" : "default"}
					label={params.row?.is_active ? "Active" : "Inactive"}
				/>
			),
		},
		{
			field: "updated_at",
			headerName: "Last Modified On",
			flex: 1,
			minWidth: 190,
			valueFormatter: (value) => formatDateTime(value) || "-",
		},
		{
			field: "updated_by_display_name",
			headerName: "Last Modified By",
			flex: 1.2,
			minWidth: 190,
			renderCell: (params) => (
				<Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", height: "100%", width: "100%" }}>
					<UserName size={24} name={params.row?.updated_by_display_name || "N/A"} />
				</Box>
			),
		},
		(canEdit || canDelete) && {
			field: "actions",
			headerName: "Actions",
			width: 130,
			sortable: false,
			filterable: false,
			renderCell: (params) => (
				<Box sx={{ display: "flex", gap: 0.5 }}>
					<Tooltip title="Edit">
						<IconButton size="small" onClick={(event) => { event.stopPropagation(); openEdit(params.row); }} disabled={!canEdit || saving}>
							<EditIcon fontSize="small" />
						</IconButton>
					</Tooltip>
					<Tooltip title="Deactivate">
						<span>
							<IconButton size="small" color="error" disabled={!canDelete || !params.row?.is_active || saving} onClick={(event) => { event.stopPropagation(); deactivateRule(params.row); }}>
								<BlockIcon fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>
				</Box>
			),
		},
	].filter(Boolean);

	const customButtons = [
		canCreate && {
			label: "Create Line Discount",
			icon: <AddIcon />,
			onClick: openCreate,
			disabled: saving,
		},
		{
			label: "Refresh Line Discounts",
			icon: <RefreshIcon />,
			onClick: fetchRules,
			disabled: loading || saving,
		},
	].filter(Boolean);

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<Box ref={containerRef} sx={{ width: "100%", display: "flex", flexDirection: "column", rowGap: 2 }}>
				{error && <Alert severity="error">{error}</Alert>}
				{loading || loadingFilters || !scopeLookupsReady ? (
					<DatagridLoading actionButtons={2} columns={9} rows={5} />
				) : (
					<Datagrid
						data={displayRows}
						columns={columns}
						rowIdField="id"
						customButtons={customButtons}
						gridSx={ADMIN_GRID_SX}
						showSearchBar
						pageSize={5}
						pageSizeOptions={[5, 10, 25, 50]}
						scrollToRowId={scrollToRowId}
						rowClick
						onRowClick={(params) => openDetail(params.row)}
						checkboxSelection={false}
						autoHeight
					/>
				)}

				{detailRow && (
					(() => {
						const scopeDetails = getLineDiscountScopeDetails(detailRow);
						return (
							<ModalWrapper
								open={!!detailRow}
								setOpen={(open) => {
									if (!open) closeDetail();
								}}
								onClose={closeDetail}
								title={detailRow.name || "Line Discount Details"}
								subtitle="Review line discount scope, percentage, and status."
								width="70vw"
								content={
									<Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
										<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 2 }}>
											<Box>
												<Typography variant="caption" color="text.secondary">Discount</Typography>
												<Typography variant="body2" fontWeight={500}>{formatDiscount(detailRow)}</Typography>
											</Box>
											<Box>
												<Typography variant="caption" color="text.secondary">Status</Typography>
												<Box sx={{ mt: 0.75 }}>
													<Chip
														size="small"
														color={detailRow?.is_active ? "success" : "default"}
														label={detailRow?.is_active ? "Active" : "Inactive"}
													/>
												</Box>
											</Box>
											<Box>
												<Typography variant="caption" color="text.secondary">Start Date</Typography>
												<Typography variant="body2" fontWeight={500}>{formatDateDisplay(detailRow?.starts_at, "N/A")}</Typography>
											</Box>
											<Box>
												<Typography variant="caption" color="text.secondary">End Date</Typography>
												<Typography variant="body2" fontWeight={500}>{formatDateDisplay(detailRow?.ends_at, "N/A")}</Typography>
											</Box>
											<Box sx={{ gridColumn: { xs: "auto", sm: "1 / -1" } }}>
												<Typography variant="caption" color="text.secondary">Scope</Typography>
												<Typography variant="body2" fontWeight={500}>
													{formatScope(detailRow, {
														customerOptions,
														siteOptions,
													})}
												</Typography>
											</Box>
											<Box>
												<Typography variant="caption" color="text.secondary">Bakery</Typography>
												<Typography variant="body2" fontWeight={500}>{scopeDetails.bakeries.join(", ") || "All Bakeries"}</Typography>
											</Box>
											<Box>
												<Typography variant="caption" color="text.secondary">Bakery Location</Typography>
												<Typography variant="body2" fontWeight={500}>{scopeDetails.locations.join(", ") || "All Bakery Locations"}</Typography>
											</Box>
											<Box>
												<Typography variant="caption" color="text.secondary">Last Modified On</Typography>
												<Typography variant="body2" fontWeight={500}>{formatDateTime(detailRow?.updated_at) || "-"}</Typography>
											</Box>
											<Box>
												<Typography variant="caption" color="text.secondary">Last Modified By</Typography>
												<Box sx={{ mt: 0.5 }}>
													<UserName size={24} name={detailRow?.updated_by_display_name || "N/A"} />
												</Box>
											</Box>
										</Box>
									</Box>
								}
								actions={
									<Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, justifyContent: "space-between", alignItems: { xs: "stretch", md: "center" }, width: "100%", gap: 2 }}>
										<Button variant="text" onClick={closeDetail}>
											Close
										</Button>
										<Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: 1 }}>
											<Button
												color="error"
												variant="contained"
												startIcon={<BlockIcon />}
												disabled={!canDelete || !detailRow?.is_active || saving}
												onClick={() => deactivateRule(detailRow)}
											>
												Deactivate
											</Button>
											<Button
												variant="contained"
												startIcon={<EditIcon />}
												disabled={!canEdit || saving}
												onClick={() => {
													openEdit(detailRow);
													closeDetail();
												}}
											>
												Edit Line Discount
											</Button>
										</Box>
									</Box>
								}
							/>
						);
					})()
				)}

				{modalOpen && (
					<Paper elevation={1} ref={formRef} sx={{ p: 3, borderRadius: 2, mt: 2, scrollMarginTop: { xs: "96px", md: "148px" } }}>
						<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
							<Box>
								<Typography variant="h6">
									{editRow ? "Edit Line Discount" : "Create Line Discount"}
								</Typography>
								<Typography variant="body1" color="text.secondary">
									Apply discounts at line level by Bakery and Bakery Location.
								</Typography>
							</Box>
							<Button variant="outlined" onClick={closeForm} disabled={saving}>
								Cancel
							</Button>
						</Box>
						<Grid container spacing={2} sx={{ pt: 1 }}>
							<Grid item xs={12} md={6}>
								<TextField fullWidth size="small" required label="Discount Name" value={form.name} onChange={(event) => updateFormField("name", event.target.value)} disabled={saving} error={!!fieldErrors.name} helperText={fieldErrors.name || `${String(form.name || "").length}/${MAX_DISCOUNT_NAME_LENGTH}`} inputProps={{ maxLength: MAX_DISCOUNT_NAME_LENGTH }} />
							</Grid>
							<Grid item xs={12} md={6}>
								<TextField fullWidth size="small" required type="text" label="Discount Percentage" value={form.discount_value} onChange={(event) => updateFormField("discount_value", event.target.value)} inputProps={{ inputMode: "decimal" }} disabled={saving} error={!!fieldErrors.discount_value} helperText={fieldErrors.discount_value} />
							</Grid>
							<Grid item xs={12} md={6}>
								<DatePicker
									label="Start Date"
									format="MM-DD-YYYY"
									value={toDayjsDate(form.starts_at)}
									onChange={(value) =>
										updateFormField(
											"starts_at",
											value?.isValid() ? value.format("YYYY-MM-DD") : ""
										)
									}
									minDate={dayjs(lineDiscountMinDate)}
									disablePast
									disabled={saving}
									slotProps={{
										textField: {
											fullWidth: true,
											size: "small",
											required: true,
											error: !!fieldErrors.starts_at,
											helperText: fieldErrors.starts_at || " ",
										},
									}}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<DatePicker
									label="End Date"
									format="MM-DD-YYYY"
									value={toDayjsDate(form.ends_at)}
									onChange={(value) =>
										updateFormField(
											"ends_at",
											value?.isValid() ? value.format("YYYY-MM-DD") : ""
										)
									}
									minDate={dayjs(form.starts_at || lineDiscountMinDate)}
									disablePast
									disabled={saving}
									slotProps={{
										textField: {
											fullWidth: true,
											size: "small",
											required: true,
											error: !!fieldErrors.ends_at,
											helperText: fieldErrors.ends_at || " ",
										},
									}}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								{renderMultiSelect({
									label: "Bakery",
									options: scopedCustomerOptions,
									value: selectedOrAllOptionsForDisplay(
										scopedCustomerOptions,
										form.customer_ids,
										false
									),
									onChange: handleCustomersChange,
									disabled: saving,
									allLabel: "All Bakeries",
									isAllSelected: broadDisplayState.customer_ids,
									error: fieldErrors.customer_ids,
								})}
							</Grid>

							<Grid item xs={12} md={6}>
								{renderMultiSelect({
									label: "Bakery Location",
									options: scopedSiteOptions,
									value: selectedOrAllOptionsForDisplay(
										scopedSiteOptions,
										form.customer_site_ids,
										false
									),
									onChange: handleLocationsChange,
									disabled: saving,
									allLabel: "All Bakery Locations",
									isAllSelected: broadDisplayState.customer_site_ids,
									error: fieldErrors.customer_site_ids,
								})}
							</Grid>

							<Grid item xs={12}>
								<FormControlLabel control={<Switch checked={form.is_active} onChange={(event) => setForm((prev) => ({ ...prev, is_active: event.target.checked }))} disabled={saving} />} label="Active" />
							</Grid>
						</Grid>
						<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
							<Button
								variant="contained"
								color="primary"
								startIcon={<AddIcon />}
								onClick={saveRule}
								disabled={saving}
							>
								{saving ? "Saving..." : editRow ? "Update Line Discount" : "Create Line Discount"}
							</Button>
						</Box>
					</Paper>
				)}
			</Box>
		</LocalizationProvider>
	);
};

export default LineDiscounts;
